import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { formatDateTime } from '@/shared/lib/format';
import { useSendResolution } from './useSendResolution';

export function MessageSendResolutionDialog({
  messageId,
  onClose,
}: {
  messageId: string;
  onClose: () => void;
}) {
  const { query, mutation, data, problem, busy, feedback, resolve, canClose } =
    useSendResolution(messageId);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && canClose()) onClose();
      }}
    >
      <DialogContent showClose={!mutation.isPending}>
        <DialogHeader>
          <DialogTitle>Versand klären</DialogTitle>
          <DialogDescription>
            Prüfe den gespeicherten Versandstand und die Bestätigung des Anbieters.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4 text-sm">
          {query.isPending && <p role="status">Versandstand wird geladen …</p>}
          {problem && (
            <p role="alert" className="text-sealwax">
              {problem.detail ?? 'Der Versandstand konnte nicht geprüft werden.'}
            </p>
          )}
          {feedback && <p role="status">{feedback}</p>}
          {data && (
            <>
              <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2">
                <dt>Kanal</dt>
                <dd>{data.channel === 'BookingCom' ? 'Booking.com' : data.channel}</dd>
                <dt>Versandstand</dt>
                <dd>{stateLabel(data.state)}</dd>
                {data.dispatchStartedAt && (
                  <>
                    <dt>Versandversuch</dt>
                    <dd>{formatDateTime(data.dispatchStartedAt)}</dd>
                  </>
                )}
                <dt>Anbieterbeleg</dt>
                <dd className="wrap-anywhere">
                  {data.providerMessageId ?? 'Keine zugeordnete Bestätigung'}
                </dd>
              </dl>
              {data.state === 'OutcomeUnknown' && (
                <p className="text-ink-soft">
                  Ohne bestätigte Annahme bleibt der Versand unklar. Ein fehlender Eintrag beim
                  Anbieter beweist nicht, dass die Nachricht nicht angekommen ist.
                </p>
              )}
              {data.state === 'OutcomeUnknown' && !data.canMarkReviewed && (
                <p role="status">Der Auftrag wird gerade geprüft. Lade den Stand danach erneut.</p>
              )}
            </>
          )}
        </DialogBody>
        <DialogFooter className="flex-wrap">
          <Button variant="ghost" disabled={busy} onClick={() => void query.refetch()}>
            Neu laden
          </Button>
          {data?.canMarkReviewed && (
            <Button
              variant="secondary"
              disabled={busy || query.isError}
              onClick={() => void resolve('ReviewedUnknown')}
            >
              Prüfung vermerken
            </Button>
          )}
          {data?.canConfirm && (
            <Button
              variant="brand"
              disabled={busy || query.isError}
              onClick={() => void resolve('Confirmed')}
            >
              Anbieterbestätigung prüfen
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function stateLabel(state: string) {
  if (state === 'Confirmed') return 'Annahme bestätigt';
  if (state === 'OutcomeUnknown') return 'Versand unklar';
  if (state === 'Pending' || state === 'Dispatching') return 'Versand läuft';
  if (state === 'Rejected') return 'Abgelehnt';
  if (state === 'Expired') return 'Abgelaufen';
  if (state === 'Suppressed') return 'Nicht gesendet';
  return 'Unbekannt';
}
